import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Copy, Download } from 'lucide-react';
import { toast } from 'sonner';

const PlaceholderImageTool = () => {
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(600);
  const [bgColor, setBgColor] = useState('#1e293b');
  const [textColor, setTextColor] = useState('#94a3b8');
  const [text, setText] = useState('');
  const [format, setFormat] = useState('svg');

  const generateSvg = () => {
    const displayText = text || `${width} x ${height}`;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <rect fill="${bgColor}" width="${width}" height="${height}"/>
      <text fill="${textColor}" font-family="Arial, sans-serif" font-size="${Math.min(width, height) / 10}" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">${displayText}</text>
    </svg>`;
  };

  const getDataUrl = () => {
    const svg = generateSvg();
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  };

  const copyUrl = () => {
    const url = `https://via.placeholder.com/${width}x${height}/${bgColor.replace('#', '')}/${textColor.replace('#', '')}?text=${encodeURIComponent(text || `${width}x${height}`)}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied!');
  };

  const copySvg = () => {
    navigator.clipboard.writeText(generateSvg());
    toast.success('SVG copied!');
  };

  const downloadImage = () => {
    const svg = generateSvg();
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `placeholder-${width}x${height}.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded!');
  };

  return (
    <div className="space-y-4" data-testid="placeholder-image-tool">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Placeholder Image</h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Width</label>
          <Input
            type="number"
            value={width}
            onChange={(e) => setWidth(parseInt(e.target.value) || 100)}
            className="bg-background/50"
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Height</label>
          <Input
            type="number"
            value={height}
            onChange={(e) => setHeight(parseInt(e.target.value) || 100)}
            className="bg-background/50"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Background</label>
          <div className="flex gap-2">
            <input
              type="color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              className="w-10 h-10 rounded cursor-pointer"
            />
            <Input
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              className="flex-1 font-mono uppercase bg-background/50"
            />
          </div>
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-1 block">Text Color</label>
          <div className="flex gap-2">
            <input
              type="color"
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
              className="w-10 h-10 rounded cursor-pointer"
            />
            <Input
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
              className="flex-1 font-mono uppercase bg-background/50"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="text-sm text-muted-foreground mb-1 block">Custom Text (optional)</label>
        <Input
          placeholder={`${width} x ${height}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="bg-background/50"
        />
      </div>

      {/* Preview */}
      <div className="flex justify-center p-4 rounded-lg bg-[#0a0a0a] border border-border">
        <img
          src={getDataUrl()}
          alt="Placeholder preview"
          style={{ maxWidth: '100%', maxHeight: '200px' }}
          data-testid="preview"
        />
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={copyUrl} className="flex-1">
          <Copy className="w-4 h-4 mr-2" /> Copy URL
        </Button>
        <Button variant="outline" onClick={copySvg} className="flex-1">
          <Copy className="w-4 h-4 mr-2" /> Copy SVG
        </Button>
        <Button onClick={downloadImage}>
          <Download className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default PlaceholderImageTool;
